import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import type {
  Aktivite,
  AktiviteSesSonuc,
  CreateAktiviteInput,
  ListAktiviteQuery,
} from '@/types/aktivite';

const QUERY_KEY = 'aktiviteler';

export function useAktiviteler(query: ListAktiviteQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('aktiviteler', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Aktivite>>(),
  });
}

export function useAktivite(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`aktiviteler/${id}`).json<{ data: Aktivite }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateAktivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAktiviteInput) =>
      api
        .post('aktiviteler', { json: input })
        .json<{ data: Aktivite }>()
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['musteriler'] }); // detay sayfasındaki listeyi tazele
      qc.invalidateQueries({ queryKey: ['takvim'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Konuşma metninden aktivite alanlarını tanır (tip/sonuç/müşteri). Kayıt YAPMAZ;
 * yalnızca formu ön-doldurmak için öneri döner.
 */
export function useAktiviteSesTani() {
  return useMutation({
    mutationFn: (metin: string) =>
      api
        .post('aktiviteler/ses-tani', { json: { metin } })
        .json<{ data: AktiviteSesSonuc }>()
        .then((r) => r.data),
  });
}

export function useUpdateAktivite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateAktiviteInput>) =>
      api
        .patch(`aktiviteler/${id}`, { json: input })
        .json<{ data: Aktivite }>()
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: ['takvim'] });
    },
  });
}

export function useDeleteAktivite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`aktiviteler/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['takvim'] });
    },
  });
}
